import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EventCard = ({ event, isRegistered, onRegister, onCancel, onViewDetails }) => {
  const {
    id,
    title,
    description,
    event_type,
    location,
    start_time,
    end_time,
    max_attendees,
    image_url,
    organizer
  } = event;

  const startDate = new Date(start_time);
  const endDate = new Date(end_time);
  const isPast = startDate < new Date();

  const eventTypeColors = {
    workshop: 'bg-blue-500',
    seminar: 'bg-purple-500',
    competition: 'bg-red-500',
    social: 'bg-green-500',
    special: 'bg-yellow-500'
  };

  const eventTypeIcons = {
    workshop: 'BookOpen',
    seminar: 'Presentation',
    competition: 'Trophy',
    social: 'Users',
    special: 'Star'
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-md overflow-hidden hover-lift transition-smooth">
      <div className="relative h-48">
        {image_url ? (
          <Image
            src={image_url}
            alt={`${title} event image`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
            <Icon name={eventTypeIcons?.[event_type]} size={64} className="text-primary/40" />
          </div>
        )}
        <div className={`absolute top-3 right-3 ${eventTypeColors?.[event_type]} text-white px-3 py-1 rounded-full text-xs font-semibold`}>
          {event_type?.charAt(0)?.toUpperCase() + event_type?.slice(1)}
        </div>
        {isPast && (
          <div className="absolute top-3 left-3 bg-muted text-muted-foreground px-3 py-1 rounded-full text-xs font-semibold">
            Past Event
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-xl font-heading font-bold text-foreground mb-2 line-clamp-2">
          {title}
        </h3>
        
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {description}
        </p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="Calendar" size={16} className="text-primary" />
            <span>{startDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="Clock" size={16} className="text-primary" />
            <span>
              {startDate?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - 
              {endDate?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </span>
          </div>

          {location && (
            <div className="flex items-center gap-2 text-sm text-foreground">
              <Icon name="MapPin" size={16} className="text-primary" />
              <span>{location}</span>
            </div>
          )}

          {max_attendees && (
            <div className="flex items-center gap-2 text-sm text-foreground">
              <Icon name="Users" size={16} className="text-primary" />
              <span>Max {max_attendees} attendees</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onViewDetails}
            className="flex-1"
            iconName="Info"
            iconPosition="left"
          >
            Details
          </Button>
          
          {!isPast && (
            isRegistered ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onCancel(id)}
                className="flex-1"
                iconName="X"
                iconPosition="left"
              >
                Cancel
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => onRegister(id)}
                className="flex-1"
                iconName="Check"
                iconPosition="left"
              >
                Register
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
